"use client";

import { useMemo, useState, useRef, useId } from "react";
import { formatCurrency } from "@/lib/formatters";

export const COLORS = [
  "#22D3EE", "#34D399", "#A3E635", "#FACC15", "#FB923C",
  "#F87171", "#F472B6", "#C084FC", "#818CF8", "#60A5FA",
];

const SIZE = 280;
const OUTER_R = 128;
const INNER_R = 80;

export interface SliceData {
  name: string;
  value: number;
  currency: string;
  color: string;
  percentage: number;
}

interface TipState {
  entry: SliceData;
  x: number;
  y: number;
}

function polar(cx: number, cy: number, r: number, deg: number) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx: number, cy: number, ro: number, ri: number, start: number, end: number) {
  const s = start + 0.8;
  const e = end - 0.8;
  if (e <= s) return null;
  const large = e - s > 180 ? 1 : 0;
  const os = polar(cx, cy, ro, s);
  const oe = polar(cx, cy, ro, e);
  const ie = polar(cx, cy, ri, e);
  const is_ = polar(cx, cy, ri, s);
  return (
    `M ${os.x} ${os.y} A ${ro} ${ro} 0 ${large} 1 ${oe.x} ${oe.y} ` +
    `L ${ie.x} ${ie.y} A ${ri} ${ri} 0 ${large} 0 ${is_.x} ${is_.y} Z`
  );
}

export function NeonDonut({ data }: { data: SliceData[] }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const filterId = useId().replace(/:/g, "");
  const [tip, setTip] = useState<TipState | null>(null);

  const slices = useMemo(() => {
    const cx = SIZE / 2;
    const cy = SIZE / 2;
    const total = data.reduce((s, d) => s + d.value, 0);
    let angle = 0;
    return data.map((d) => {
      const sweep = (d.value / total) * 360;
      const path = arcPath(cx, cy, OUTER_R, INNER_R, angle, angle + sweep);
      angle += sweep;
      return { ...d, path };
    });
  }, [data]);

  const onMove = (e: React.MouseEvent, entry: SliceData) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    setTip({ entry, x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <div className="relative shrink-0" style={{ width: SIZE, height: SIZE }}>
      <svg ref={svgRef} width={SIZE} height={SIZE} style={{ overflow: "visible" }}>
        <defs>
          <filter id={filterId} x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur1" />
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur2" />
            <feMerge>
              <feMergeNode in="blur1" />
              <feMergeNode in="blur2" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {slices.map((s, i) =>
          s.path ? (
            <path
              key={i}
              d={s.path}
              fill={s.color}
              fillOpacity={0.06}
              stroke={s.color}
              strokeWidth={1.5}
              filter={`url(#${filterId})`}
              style={{ cursor: "pointer" }}
              onMouseEnter={(e) => onMove(e, s)}
              onMouseMove={(e) => onMove(e, s)}
              onMouseLeave={() => setTip(null)}
            />
          ) : null
        )}
      </svg>

      {tip && (
        <div
          className="absolute pointer-events-none z-10 bg-surface-2 border border-border rounded-lg px-3 py-2 text-xs shadow-xl whitespace-nowrap"
          style={{
            left: tip.x < SIZE / 2 ? tip.x + 16 : "auto",
            right: tip.x >= SIZE / 2 ? SIZE - tip.x + 16 : "auto",
            top: tip.y,
            transform: "translateY(-50%)",
          }}
        >
          <p className="text-muted mb-0.5">{tip.entry.name}</p>
          <p className="font-semibold text-text">
            {formatCurrency(tip.entry.value, tip.entry.currency)}{" "}
            <span className="text-muted font-normal">({tip.entry.percentage.toFixed(2)}%)</span>
          </p>
        </div>
      )}
    </div>
  );
}

export function DonutCard({
  title,
  data,
  mounted,
}: {
  title: string;
  data: SliceData[];
  mounted: boolean;
}) {
  return (
    <div className="bg-chart-net-worth-bg rounded-xl p-6 flex flex-col">
      <p className="text-base font-bold text-text">{title}</p>
      <div className="mt-5 flex flex-col items-center gap-8 sm:flex-row">
        {mounted && data.length > 0 ? (
          <NeonDonut data={data} />
        ) : (
          <div
            className="shrink-0 flex items-center justify-center text-muted text-xs"
            style={{ width: SIZE, height: SIZE }}
          >
            —
          </div>
        )}
        <div className="w-full flex-1 min-w-0 space-y-3">
          {data.map((entry) => (
            <div key={entry.name} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="h-2.5 w-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-muted text-xs truncate">{entry.name}</span>
              </div>
              <span className="text-text text-xs tabular-nums shrink-0">
                {formatCurrency(entry.value, entry.currency)}{" "}
                <span className="text-muted">({entry.percentage.toFixed(2)}%)</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
